import { LabelPrintPreferences } from "@bcc/components/label-print-preferences/label-print-preferences";
import { OrderConfiguration } from "@bcc/components/order-configuration/order-configuration";
import { ProfilePreferences } from "@bcc/components/profile-preferences/profile-preferences";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bcc/components/ui/tabs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  setPreferencesViewConnectionsTabTriggerTestId,
  setPreferencesViewContainerTestId,
  setPreferencesViewMailingTabTriggerTestId,
  setPreferencesViewPrintingTabTriggerTestId,
} from "./preferences-view.utils";

type PreferencesTabValue = "mailing" | "printing" | "connections";

const getTabFromSearch = (search: string): PreferencesTabValue => {
  const tabParam = new URLSearchParams(search).get("tab");
  if (tabParam === "printing") return "printing";
  if (tabParam === "connections") return "connections";
  return "mailing";
};

const normalizeTab = (tab: string): PreferencesTabValue => {
  if (tab === "printing") return "printing";
  if (tab === "connections") return "connections";
  return "mailing";
};

export function PreferencesView() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab = getTabFromSearch(location.search);

  useEffect(() => {
    const currentTab = new URLSearchParams(location.search).get("tab");
    if (currentTab === activeTab) {
      return;
    }
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("tab", activeTab);
    navigate(
      {
        pathname: location.pathname,
        search: `?${nextSearchParams.toString()}`,
      },
      { replace: true }
    );
  }, [activeTab, location.pathname, location.search, navigate]);

  const handleTabChange = (nextTab: string) => {
    const normalizedTab = normalizeTab(nextTab);
    if (normalizedTab === activeTab) {
      return;
    }
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("tab", normalizedTab);
    navigate({
      pathname: location.pathname,
      search: `?${nextSearchParams.toString()}`,
    });
  };

  return (
    <div data-testid={setPreferencesViewContainerTestId()} className="space-y-6">
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="mailing" data-testid={setPreferencesViewMailingTabTriggerTestId()}>
            {t("views.preferences.tabs.mailing")}
          </TabsTrigger>
          <TabsTrigger value="printing" data-testid={setPreferencesViewPrintingTabTriggerTestId()}>
            {t("views.preferences.tabs.printing")}
          </TabsTrigger>
          <TabsTrigger value="connections" data-testid={setPreferencesViewConnectionsTabTriggerTestId()}>
            {t("views.preferences.tabs.connections")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mailing" className="space-y-6 mt-6">
          <ProfilePreferences />
        </TabsContent>

        <TabsContent value="printing" className="space-y-6 mt-6">
          <LabelPrintPreferences />
        </TabsContent>

        <TabsContent value="connections" className="space-y-6 mt-6">
          <OrderConfiguration />
        </TabsContent>
      </Tabs>
    </div>
  );
}
